/**
 * WHAT WILL COME OUT, BEFORE A TICKET IS SPENT.
 *
 * Not a drawing of a ticket: the real driver runs `printTicket` with this printer's profile into a
 * private simulated printer, and the preview is what that printer laid out — the same bytes, the
 * same print area, wrapping and alignment. If a margin pushes the event name onto two lines, the
 * preview shows two lines because the bytes say so, not because a mock-up guessed.
 */

import { MasungPrinter, type MasungTicketData } from '../masung-printer/printer';
import { SimPrinter, createSimulatedUsb } from '../masung-printer/simulator';
import type { PrinterProfile } from '../masung-printer/profile';

export interface PreviewLine { text: string; kind: 'text' | 'qr' | 'barcode' | 'picture'; mag: number }

export async function renderPreview(profile: PrinterProfile | null, ticket: MasungTicketData): Promise<{ lines: PreviewLine[]; cols: number }> {
  const headDots = profile?.headDots ?? 576;
  const sim = new SimPrinter({
    headDots,
    // The simulator draws a QR for the method this firmware speaks — mirror the profile.
    productName: profile?.qrMethod === 'escpos' ? 'IP1000 Printer USB001' : 'Masung Printer',
    statusAnswers: true,
  });
  const p = new MasungPrinter();
  p.useUsb(createSimulatedUsb(sim));
  await p.autoConnect('80');
  p.applyProfile(profile);
  const mode = profile?.paperType === 'continuous' ? 'cut' : 'tear';
  await p.printTicket({ ...ticket, mode, showHeader: profile?.showHeader ?? true });
  await p.disconnect();
  const lines: PreviewLine[] = sim.strip
    .filter((e) => e.kind === 'line')
    .map((e) => ({
      text: e.text,
      mag: e.mag ?? 1,
      kind: /QR \(/.test(e.text) ? 'qr' : /barcode/.test(e.text) ? 'barcode' : /picture/.test(e.text) ? 'picture' : 'text',
    }));
  return { lines, cols: Math.floor(headDots / 12) };
}
